import React from 'react'
import { useWindowDimensions } from 'react-native'

import { buildComponent } from 'src/components/factory'
import { useAppearance } from 'src/hooks/use-appearance'
import { useAudioConstraints } from 'src/hooks/use-audio-constraints'
import { useStoreState } from 'src/hooks/state'
import { Category, LanguageRecord } from 'src/state/models/audio'

import { AudioFavorites } from 'src/resources/icons'
import * as Elements from 'src/components/elements'
import * as Semantics from 'src/components/semantics'
import * as UI from 'native-base'
import { HeaderAudioIcon } from 'src/screens/connected/audio/components'

export const Categories = buildComponent()
  .withLocale('screens.connected.categories.screen')
  .withLifecycle(() => {
    const bookingDisabled = useStoreState(
      (store) => store.space.bookingDisabled,
    )
    useAppearance({
      action: bookingDisabled ? HeaderAudioIcon : 'back',
    })

    const categories = useStoreState(
      (store) => store.space.info?.subscription?.audio?.categories ?? [],
    )

    // Récupérer les favoris pour savoir s'il faut afficher la section favoris
    const { favorites } = useAudioConstraints()
    const hasFavorites = favorites.length > 0

    const { height } = useWindowDimensions()
    const currentLocale = useStoreState(
      (store) => store.utils.device.locale,
    ) as keyof LanguageRecord<string>

    const [maxCardHeight, setMaxCardHeight] = React.useState<number | null>(null)
    const [isMeasuring, setIsMeasuring] = React.useState(true)
    const measuredHeights = React.useRef<number[]>([])

    const handleCardLayout = (event: any) => {
      if (!isMeasuring) return

      const { height } = event.nativeEvent.layout
      measuredHeights.current.push(height)

      // Once we've measured all cards, set the max height
      const totalCards = categories.filter((c: Category) => !c.parent).length + (hasFavorites ? 1 : 0)
      if (measuredHeights.current.length === totalCards) {
        const max = Math.max(...measuredHeights.current)
        setMaxCardHeight(max)
        setIsMeasuring(false)
      }
    }

    // Fonction pour déterminer la route de destination selon le type
    const getDestinationRoute = (categoryType: string) => {
      switch (categoryType) {
        case 'book':
          return '/audio/audiobooks'
        case 'theme':
          return '/audio/sophrology'
        case 'background':
          return '/audio/backgrounds'
        default:
          return '/category'
      }
    }

    // Fonction pour vérifier si une catégorie a des enfants
    const hasChildren = (categorySlug: string) => {
      return categories.some((c: Category) => c.parent?.slug === categorySlug)
    }

    return {
      categories,
      getDestinationRoute,
      hasChildren,
      hasFavorites,
      height,
      currentLocale,
      maxCardHeight,
      handleCardLayout,
      isMeasuring,
      bookingDisabled,
    }
  })
  .withRender(({ lifecycle, locale }) => (
    <>
      <Semantics.PaddedScrollView
        centered
        px={2}
        mx={[0, 'auto']}
        width="100%"
        maxWidth={['100%', 600]}
      >
        {lifecycle.categories
          .filter((category: Category) => !category.parent)
          .map((category: Category, index: number) => {
            const hasSubcategories = lifecycle.hasChildren(category.slug)
            const categoryTitle =
              category.short[lifecycle.currentLocale] || category.short.fr
            const categoryDescription =
              category.long[lifecycle.currentLocale] || category.long.fr

            return (
              <UI.Box
                key={category.slug || `category-${index}`}
                onLayout={lifecycle.handleCardLayout}
                opacity={lifecycle.isMeasuring ? 0 : 1}
              >
                <Elements.AudioOptionCategory
                  testID={`category-${category.slug}`}
                  to={
                    hasSubcategories
                      ? '/category/detail'
                      : lifecycle.getDestinationRoute(category.type)
                  }
                  params={
                    hasSubcategories
                      ? { slug: category.slug }
                      : { category: category.slug, categoryType: category.type }
                  }
                  show={true}
                  imageSource={`images/categories/${category.slug}/${category.image ?? 'image'
                    }.webp`}
                  title={categoryTitle}
                  description={categoryDescription}
                  minHeight={lifecycle.isMeasuring ? undefined : lifecycle.maxCardHeight ?? undefined}
                />
              </UI.Box>
            )
          })}

        {lifecycle.hasFavorites && (
          <UI.Box
            onLayout={lifecycle.handleCardLayout}
            opacity={lifecycle.isMeasuring ? 0 : 1}
          >
            <Elements.AudioOptionCategory
              key="favorites"
              to="/audio/favorites"
              show={true}
              title={locale('favorites.title')}
              description={locale('favorites.description')}
              Icon={AudioFavorites}
              minHeight={lifecycle.isMeasuring ? undefined : lifecycle.maxCardHeight ?? undefined}
            />
          </UI.Box>
        )}
      </Semantics.PaddedScrollView>

      {lifecycle.bookingDisabled && <Elements.MyAccountButton />}
    </>
  ))
